import { useCallback, useEffect, useState } from "react";
import { api, type Gallery, type GalleryAsset, type Client, type Booking, type ProofingResults, type GalleryAnalytics } from "../lib/api";
import { navigate } from "../lib/route";
import type { PublishResult } from "../lib/api";
import { CullTab } from "../components/gallery/CullTab";
import { ProofingTab } from "../components/gallery/ProofingTab";
import { GalleryStatsTab } from "../components/gallery/StatsTab";

type UploadProgress = {
  id: string;
  filename: string;
  status: "queued" | "uploading" | "done" | "error";
  error?: string;
};

type AssetEditState = {
  assetId: string;
  name: string;
  description: string;
  saving: boolean;
  error: string | null;
};

interface Props {
  slug: string;
}

export function GalleryDetailPage({ slug }: Props) {
  const [gallery, setGallery] = useState<Gallery | null>(null);
  const [assets, setAssets] = useState<GalleryAsset[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [assetEdit, setAssetEdit] = useState<AssetEditState | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<"payment_required" | null>(null);
  const [linkedBooking, setLinkedBooking] = useState<Booking | null>(null);
  const [activeTab, setActiveTab] = useState<"photos" | "proofing" | "cull" | "stats">("photos");
  const [cull, setCull] = useState<Awaited<ReturnType<typeof api.getGalleryCull>> | null>(null);
  const [cullLoading, setCullLoading] = useState(false);
  const [cullError, setCullError] = useState<string | null>(null);
  const [proofing, setProofing] = useState<ProofingResults | null>(null);
  const [proofingLoading, setProofingLoading] = useState(false);
  const [proofingError, setProofingError] = useState<string | null>(null);
  const [galleryAnalytics, setGalleryAnalytics] = useState<GalleryAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Share URL state
  const [subdomainHost, setSubdomainHost] = useState<string>('');
  const [portfolioSlug, setPortfolioSlug] = useState<string>('');

  // Client portal link state
  const [portalTokenUrl, setPortalTokenUrl] = useState<string | null>(null);
  const [generatingPortal, setGeneratingPortal] = useState(false);

  // Settings form state (mirrors gallery fields)
  const [settingsTitle, setSettingsTitle] = useState("");
  const [settingsDescription, setSettingsDescription] = useState("");
  const [settingsClientID, setSettingsClientID] = useState("");
  const [settingsIsPublic, setSettingsIsPublic] = useState(false);
  const [settingsAllowDownloads, setSettingsAllowDownloads] = useState(false);
  const [settingsLayout, setSettingsLayout] = useState("masonry");
  const [settingsSharePassword, setSettingsSharePassword] = useState("");
  const [settingsMode, setSettingsMode] = useState("delivered");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [g, a, cls] = await Promise.all([
        api.getGallery(slug),
        api.listGalleryAssets(slug),
        api.listClients(),
      ]);
      setGallery(g);
      setAssets(a);
      setClients(cls);
      // Seed settings form
      setSettingsTitle(g.Title);
      setSettingsDescription(g.Description);
      setSettingsClientID(g.ClientID);
      setSettingsIsPublic(g.IsPublic);
      setSettingsAllowDownloads(g.AllowDownloads);
      setSettingsLayout(g.Layout ?? "masonry");
      setSettingsMode(g.Mode ?? "delivered");
      setSettingsSharePassword("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch runtime config and portfolio slug for share URL construction.
  useEffect(() => {
    api.runtimeConfig().then((c) => setSubdomainHost(c.subdomain_host ?? '')).catch(() => {});
    api.getMyPortfolio().then((p) => setPortfolioSlug(p.Slug)).catch(() => {});
  }, []);

  // Load the linked booking when gallery has a BookingID, to show payment status.
  useEffect(() => {
    if (gallery?.BookingID) {
      api.listBookings().then(bs => {
        const b = bs.find(b => b.ID === gallery.BookingID);
        setLinkedBooking(b ?? null);
      }).catch(() => {});
    } else {
      setLinkedBooking(null);
    }
  }, [gallery?.BookingID]);

  // Load proofing results when the proofing tab is activated.
  useEffect(() => {
    if (activeTab !== "proofing" || !gallery) return;
    setProofingLoading(true);
    setProofingError(null);
    api.getGalleryProofing(gallery.Slug)
      .then(setProofing)
      .catch((err) => setProofingError(err instanceof Error ? err.message : "Failed to load proofing data"))
      .finally(() => setProofingLoading(false));
  }, [activeTab, gallery]);

  // Load cull data when the cull tab is activated.
  useEffect(() => {
    if (activeTab !== "cull" || !gallery) return;
    setCullLoading(true);
    setCullError(null);
    api.getGalleryCull(gallery.Slug)
      .then(setCull)
      .catch((err) => setCullError(err instanceof Error ? err.message : "Failed to load cull data"))
      .finally(() => setCullLoading(false));
  }, [activeTab, gallery]);

  // Load analytics when the stats tab is activated.
  useEffect(() => {
    if (activeTab !== "stats" || !gallery) return;
    setAnalyticsLoading(true);
    api.getGalleryAnalytics(gallery.Slug)
      .then(setGalleryAnalytics)
      .catch(() => {})
      .finally(() => setAnalyticsLoading(false));
  }, [activeTab, gallery]);

  async function handleSaveSettings(e: React.FormEvent) {
    e.preventDefault();
    if (!gallery) return;
    setSaving(true);
    setSettingsError(null);
    try {
      const updated = await api.updateGallery(gallery.ID, {
        title: settingsTitle,
        description: settingsDescription,
        client_id: settingsClientID,
        is_public: settingsIsPublic,
        allow_downloads: settingsAllowDownloads,
        layout: settingsLayout,
        share_password: settingsSharePassword,
        mode: settingsMode,
      });
      setGallery(updated);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const linkedClient = clients.find(c => c.ID === gallery?.ClientID);
  const canNotify = !!linkedClient?.Email;

  async function handlePublish() {
    if (!gallery) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const result: PublishResult = await api.publishGallery(gallery.Slug, canNotify);
      setGallery(result.gallery);
      if (result.email_sent) {
        setPublishResult(`Gallery live! Email sent to ${result.client_email}`);
      } else {
        setPublishResult("Gallery is now public.");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Publish failed";
      if (msg.includes("payment")) {
        setPublishError("payment_required");
      } else {
        setError(msg);
      }
    } finally {
      setPublishing(false);
    }
  }

  const generatePortalLink = useCallback(async () => {
    if (!gallery?.ClientID) return;
    setGeneratingPortal(true);
    try {
      const result = await api.generateClientToken(gallery.ClientID);
      setPortalTokenUrl(result.token_url);
    } catch {
      // ignore — user can retry
    } finally {
      setGeneratingPortal(false);
    }
  }, [gallery?.ClientID]);

  const revokePortalLink = useCallback(async () => {
    if (!gallery?.ClientID) return;
    try {
      await api.revokeClientTokens(gallery.ClientID);
      setPortalTokenUrl(null);
    } catch {
      // ignore
    }
  }, [gallery?.ClientID]);

  function openAssetEdit(a: import("../lib/api").GalleryAsset) {
    setAssetEdit({ assetId: a.id, name: a.name ?? "", description: a.description ?? "", saving: false, error: null });
  }

  async function handleSaveAsset(e: React.FormEvent) {
    e.preventDefault();
    if (!assetEdit || !gallery) return;
    setAssetEdit((s) => s ? { ...s, saving: true, error: null } : s);
    try {
      const updated = await api.updateGalleryAsset(gallery.Slug, assetEdit.assetId, {
        name: assetEdit.name,
        description: assetEdit.description,
      });
      setAssets((prev) => prev.map((a) => a.id === updated.id ? updated : a));
      setAssetEdit(null);
    } catch (err) {
      setAssetEdit((s) => s ? { ...s, saving: false, error: err instanceof Error ? err.message : "Save failed" } : s);
    }
  }

  async function handleSetCover(assetId: string) {
    if (!gallery) return;
    try {
      const updated = await api.updateGallery(gallery.ID, { cover_asset_id: assetId });
      setGallery(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set cover');
    }
  }

  async function handleDeleteAsset(assetId: string, filename: string) {
    if (!gallery) return;
    if (!confirm(`Remove "${filename}" from this gallery? This cannot be undone.`)) return;
    try {
      await api.deleteGalleryAsset(gallery.Slug, assetId);
      setAssets((prev) => prev.filter((a) => a.id !== assetId));
      setGallery((g) => g ? { ...g, AssetCount: Math.max(0, g.AssetCount - 1) } : g);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }

  async function handleFiles(files: FileList | File[]) {
    if (!gallery) return;
    const list = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;

    const queued: UploadProgress[] = list.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
      filename: f.name,
      status: "queued",
    }));
    setUploads((prev) => [...prev, ...queued]);

    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      const upload = queued[i];
      setUploads((prev) =>
        prev.map((u) => (u.id === upload.id ? { ...u, status: "uploading" } : u)),
      );
      try {
        await api.uploadGalleryAsset(gallery.ID, f);
        setUploads((prev) =>
          prev.map((u) => (u.id === upload.id ? { ...u, status: "done" } : u)),
        );
      } catch (err) {
        setUploads((prev) =>
          prev.map((u) =>
            u.id === upload.id
              ? { ...u, status: "error", error: err instanceof Error ? err.message : "failed" }
              : u,
          ),
        );
      }
    }
    await load();
    setTimeout(() => setUploads((prev) => prev.filter((u) => u.status === "error")), 2500);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }

  if (loading) {
    return <p style={{ color: "var(--muted)" }}>Loading gallery…</p>;
  }
  if (error || !gallery) {
    return (
      <div>
        <button className="btn-secondary" onClick={() => navigate("/galleries")}>
          ← Galleries
        </button>
        <div className="alert alert-error" style={{ marginTop: "1rem" }}>
          {error ?? "Gallery not found"}
        </div>
      </div>
    );
  }

  const shareBaseUrl = subdomainHost && portfolioSlug
    ? 'https://' + portfolioSlug + '.' + subdomainHost
    : window.location.origin;
  const shareFullUrl = shareBaseUrl + '/share/g/' + gallery.Slug;
  const paymentPending = linkedBooking && linkedBooking.PriceCents > 0 && !linkedBooking.BalancePaid;

  return (
    <div>
      <button
        className="btn-secondary"
        onClick={() => navigate("/galleries")}
        style={{ marginBottom: "1rem" }}
      >
        ← Galleries
      </button>

      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 style={{ margin: 0 }}>{gallery.Title}</h1>
          {linkedBooking && linkedBooking.PriceCents > 0 && (
            <div style={{ fontSize: "0.8rem", color: linkedBooking.BalancePaid ? "#10b981" : "#f59e0b", marginTop: "0.25rem" }}>
              {linkedBooking.BalancePaid ? "✓ Payment confirmed" : "⚠ Payment pending"}
              {!linkedBooking.BalancePaid && (
                <a href="/app/bookings" style={{ color: "#f59e0b", marginLeft: "0.5rem" }}>Mark as paid →</a>
              )}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          {gallery && !gallery.IsPublic && (
            <button
              className={paymentPending ? "btn-ghost" : "btn-primary"}
              onClick={handlePublish}
              disabled={publishing}
              title={paymentPending ? "Payment not yet confirmed" : undefined}
              style={{ whiteSpace: "nowrap" }}
            >
              {publishing ? "Publishing…" : canNotify ? `Publish & notify ${linkedClient!.DisplayName}` : "Publish gallery"}
            </button>
          )}
          {gallery.IsPublic && (
            <a
              href={shareFullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Open share page
            </a>
          )}
          <button
            className="btn-ghost"
            onClick={() => setSettingsOpen((v) => !v)}
            style={{ padding: "0.4rem 0.75rem" }}
          >
            {settingsOpen ? "Hide settings" : "Settings"}
          </button>
        </div>
      </div>
      {publishResult && (
        <div className="alert" style={{ marginTop: "0.5rem", background: "color-mix(in srgb, var(--accent) 12%, transparent)", borderColor: "var(--accent)", color: "var(--text)" }}>
          {publishResult}
        </div>
      )}
      {publishError === "payment_required" && (
        <div className="alert" style={{ borderColor: "#f59e0b", background: "color-mix(in srgb, #f59e0b 10%, transparent)", marginTop: "0.5rem" }}>
          <strong>Payment not confirmed yet.</strong>{" "}
          Mark the booking as paid before publishing the gallery.{" "}
          {gallery.BookingID && (
            <a href="/app/bookings" style={{ color: "#f59e0b" }}>Open bookings →</a>
          )}
        </div>
      )}
      <p style={{ color: "var(--muted)", marginTop: "0.25rem" }}>
        {assets.length} photo{assets.length === 1 ? "" : "s"} ·{" "}
        {gallery.IsPublic ? "Public" : "Private"} ·{" "}
        {gallery.AllowDownloads ? "Downloads allowed" : "Downloads disabled"}
      </p>

      {gallery.IsPublic && (
        <div style={{ marginBottom: "0.75rem", padding: "0.5rem 0.75rem", background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 6, display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", maxWidth: "100%", boxSizing: "border-box" }}>
          <span style={{ color: "var(--muted)", fontSize: "0.875rem" }}>Share link:</span>
          <code style={{ fontSize: "0.875rem", flex: "1 1 200px", minWidth: 0, wordBreak: "break-all", color: "var(--text)" }}>{shareFullUrl}</code>
          <button
            className="btn-ghost"
            style={{ fontSize: "0.8rem", padding: "0.25rem 0.6rem" }}
            onClick={() => void navigator.clipboard.writeText(shareFullUrl)}
          >
            Copy
          </button>
          <a href={shareFullUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.875rem" }}>Open →</a>
        </div>
      )}

      {/* Client portal link — only shown when a client is linked */}
      {gallery?.ClientID && linkedClient && (
        <div style={{ marginBottom: "0.75rem", padding: "0.65rem 0.85rem", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
            <span style={{ fontSize: "0.875rem", color: "var(--muted)" }}>
              Client portal for <strong style={{ color: "var(--text)" }}>{linkedClient.DisplayName}</strong>:
            </span>
            {portalTokenUrl ? (
              <>
                <input
                  readOnly
                  value={portalTokenUrl}
                  style={{ flex: "1 1 200px", fontSize: "0.8rem", minWidth: 0 }}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button className="btn-ghost" style={{ fontSize: "0.8rem", padding: "0.25rem 0.6rem" }} onClick={() => void navigator.clipboard.writeText(portalTokenUrl)}>Copy</button>
                <button className="btn-ghost" style={{ fontSize: "0.8rem", padding: "0.25rem 0.6rem", color: "#ef4444" }} onClick={() => void revokePortalLink()}>Revoke</button>
                <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>Valid 30 days</span>
              </>
            ) : (
              <button className="btn-ghost" style={{ fontSize: "0.8rem", padding: "0.25rem 0.6rem" }} disabled={generatingPortal} onClick={() => void generatePortalLink()}>
                {generatingPortal ? "Generating…" : "Generate access link"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border, #2c3640)", marginBottom: "1rem" }}>
        {(["photos", "proofing", "cull", "stats"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: "none",
              border: "none",
              borderBottom: activeTab === tab ? "2px solid var(--accent, #10b981)" : "2px solid transparent",
              padding: "0.5rem 1rem",
              cursor: "pointer",
              fontWeight: activeTab === tab ? 600 : 400,
              color: activeTab === tab ? "var(--accent, #10b981)" : "var(--muted)",
              fontSize: "0.9rem",
              textTransform: "capitalize",
            }}
          >
            {tab === "photos" ? `Photos (${assets.length})` : tab === "proofing" ? "Proofing" : tab === "cull" ? "Cull" : "Stats"}
          </button>
        ))}
      </div>

      {settingsOpen && (
        <form onSubmit={handleSaveSettings} className="card" style={{ marginBottom: "1.25rem" }}>
          <h3 style={{ marginTop: 0 }}>Gallery settings</h3>
          {settingsError && <div className="alert alert-error" style={{ marginBottom: "0.75rem" }}>{settingsError}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            <div className="form-group">
              <label>Title</label>
              <input value={settingsTitle} onChange={(e) => setSettingsTitle(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Client</label>
              <select value={settingsClientID} onChange={(e) => setSettingsClientID(e.target.value)}>
                <option value="">— none —</option>
                {clients.map((c) => (
                  <option key={c.ID} value={c.ID}>{c.DisplayName}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginTop: "0.5rem" }}>
            <label>Description</label>
            <textarea rows={2} value={settingsDescription} onChange={(e) => setSettingsDescription(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={settingsIsPublic}
                onChange={(e) => setSettingsIsPublic(e.target.checked)}
                style={{ width: "auto" }}
              />
              Public (share page visible)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <input
                type="checkbox"
                checked={settingsAllowDownloads}
                onChange={(e) => setSettingsAllowDownloads(e.target.checked)}
                style={{ width: "auto" }}
              />
              Allow downloads
            </label>
          </div>
          <div className="form-group" style={{ marginTop: "0.75rem" }}>
            <label>Gallery layout</label>
            <select value={settingsLayout} onChange={(e) => setSettingsLayout(e.target.value)}>
              <option value="masonry">Masonry + lightbox (default)</option>
              <option value="slideout">Grid with slide-out details</option>
              <option value="parallax">Cinematic parallax scroll</option>
              <option value="direct">Justified rows — opens detail page</option>
              <option value="instagram">Instagram grid</option>
              <option value="flickr">Flickr justified + lightbox</option>
            </select>
            <small style={{ color: "var(--muted)" }}>How photos appear on the public share page</small>
          </div>
          <div className="form-group" style={{ marginTop: "0.75rem" }}>
            <label>Gallery type</label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {(["delivered", "proofing"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setSettingsMode(m)}
                  style={{
                    padding: "0.35rem 0.9rem",
                    borderRadius: ".4rem",
                    border: settingsMode === m ? "2px solid var(--primary, #6366f1)" : "1px solid var(--border, #2c3640)",
                    background: settingsMode === m ? "var(--primary-subtle, rgba(99,102,241,.1))" : "transparent",
                    cursor: "pointer",
                    fontSize: ".875rem",
                    color: "var(--text)",
                    textTransform: "capitalize",
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
            <small style={{ color: "var(--muted)" }}>
              Proofing galleries appear in the client portal's Proofing tab; Delivered galleries appear in the Galleries tab.
            </small>
          </div>
          <div className="form-group" style={{ marginTop: "0.75rem" }}>
            <label>Gallery password (optional)</label>
            <input
              type="password"
              placeholder="Leave blank for public access"
              value={settingsSharePassword}
              onChange={(e) => setSettingsSharePassword(e.target.value)}
              autoComplete="new-password"
            />
            <small style={{ color: "var(--muted)" }}>
              Clients must enter this password to view the gallery share page.
            </small>
          </div>
          <button type="submit" className="btn-primary" disabled={saving} style={{ marginTop: "1rem" }}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </form>
      )}

      {activeTab === "proofing" && (
        <ProofingTab
          proofing={proofing}
          loading={proofingLoading}
          error={proofingError}
          assets={assets}
        />
      )}

      {activeTab === "cull" && (
        <CullTab
          gallerySlug={gallery.Slug}
          data={cull}
          loading={cullLoading}
          error={cullError}
          onAssetDeleted={async (assetId) => {
            try {
              await api.deleteGalleryAsset(gallery.Slug, assetId);
              setCull((prev) => prev ? { ...prev, assets: prev.assets.filter(a => a.id !== assetId), count: prev.count - 1 } : prev);
              setAssets((prev) => prev.filter((a) => a.id !== assetId));
            } catch (e) {
              setCullError(e instanceof Error ? e.message : "Delete failed");
            }
          }}
        />
      )}

      {activeTab === "stats" && (
        <GalleryStatsTab analytics={galleryAnalytics} loading={analyticsLoading} />
      )}

      {activeTab === "photos" && <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragOver ? "var(--primary)" : "var(--border, #2c3640)"}`,
          background: dragOver ? "rgba(16,185,129,0.08)" : "transparent",
          borderRadius: 8,
          padding: "2rem 1rem",
          textAlign: "center",
          margin: "1rem 0",
          transition: "background 120ms, border-color 120ms",
        }}
      >
        <p style={{ margin: "0 0 0.75rem", color: "var(--muted)" }}>
          Drag photos here, or
        </p>
        <label className="btn-primary" style={{ cursor: "pointer", display: "inline-block" }}>
          <input
            type="file"
            multiple
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
          Choose files
        </label>
        <p style={{ margin: "0.75rem 0 0", color: "var(--muted)", fontSize: "0.85rem" }}>
          JPEG/PNG/HEIC up to 30MB. We resize + watermark automatically.
        </p>
      </div>}

      {activeTab === "photos" && uploads.length > 0 && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          {uploads.map((u) => (
            <div
              key={u.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "0.25rem 0",
                fontSize: "0.9rem",
              }}
            >
              <span>{u.filename}</span>
              <span style={{ color: u.status === "error" ? "var(--danger, #f87171)" : "var(--muted)" }}>
                {u.status === "done" ? "✓ done" : u.status === "error" ? `✗ ${u.error}` : u.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {activeTab === "photos" && error && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {activeTab === "photos" && assetEdit && (
        <div className="card" style={{ marginBottom: "1rem", padding: "1rem" }}>
          <form onSubmit={handleSaveAsset}>
            <h4 style={{ margin: "0 0 0.75rem" }}>Foto details bewerken</h4>
            {assetEdit.error && <div className="alert alert-error" style={{ marginBottom: "0.5rem" }}>{assetEdit.error}</div>}
            <div className="form-group" style={{ marginBottom: "0.5rem" }}>
              <label>Name</label>
              <input
                value={assetEdit.name}
                onChange={(e) => setAssetEdit((s) => s ? { ...s, name: e.target.value } : s)}
                placeholder="Titel van de foto"
              />
            </div>
            <div className="form-group" style={{ marginBottom: "0.75rem" }}>
              <label>Description</label>
              <textarea
                rows={2}
                value={assetEdit.description}
                onChange={(e) => setAssetEdit((s) => s ? { ...s, description: e.target.value } : s)}
                placeholder="Bijschrift of beschrijving"
              />
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button type="submit" className="btn-primary" disabled={assetEdit.saving}>
                {assetEdit.saving ? "Saving…" : "Save"}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setAssetEdit(null)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === "photos" && assets.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0, color: "var(--muted)" }}>
            No photos uploaded yet. Drop some files above to get started.
          </p>
        </div>
      ) : activeTab === "photos" ? (
        <div
          style={{
            display: "grid",
            gap: "0.5rem",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          }}
        >
          {assets.map((a) => (
            <div
              key={a.id}
              style={{
                display: "flex",
                flexDirection: "column",
                background: "var(--card-bg, #131b24)",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <div style={{ position: "relative", aspectRatio: "1 / 1" }}>
                <a
                  href={a.web_url || a.original_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "block", width: "100%", height: "100%" }}
                >
                  <img
                    src={a.thumb_url}
                    alt={a.name || a.filename}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </a>
                <div style={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 2 }}>
                  {a.id === gallery.CoverAssetID ? (
                    <span style={{ background: "rgba(0,0,0,0.6)", borderRadius: 4, padding: "0.2rem 0.4rem", fontSize: ".7rem", color: "var(--accent)", fontWeight: 600, lineHeight: 1 }}>★ Cover</span>
                  ) : (
                    <button
                      onClick={() => handleSetCover(a.id)}
                      title="Set as gallery cover"
                      style={{
                        background: "rgba(0,0,0,0.6)",
                        border: "none",
                        borderRadius: 4,
                        color: "#e2e8f0",
                        cursor: "pointer",
                        padding: "0.2rem 0.4rem",
                        fontSize: ".7rem",
                        lineHeight: 1,
                      }}
                    >
                      ☆
                    </button>
                  )}
                  <button
                    onClick={() => openAssetEdit(a)}
                    title="Edit photo"
                    style={{
                      background: "rgba(0,0,0,0.6)",
                      border: "none",
                      borderRadius: 4,
                      color: "#e2e8f0",
                      cursor: "pointer",
                      padding: "0.2rem 0.4rem",
                      fontSize: "0.75rem",
                      lineHeight: 1,
                      opacity: 1,
                    }}
                  >
                    ✎
                  </button>
                  <button
                    onClick={() => handleDeleteAsset(a.id, a.filename)}
                    title="Delete photo"
                    style={{
                      background: "rgba(0,0,0,0.6)",
                      border: "none",
                      borderRadius: 4,
                      color: "#f87171",
                      cursor: "pointer",
                      padding: "0.2rem 0.4rem",
                      fontSize: "0.85rem",
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
              {a.name && (
                <div style={{ padding: "0.25rem 0.4rem", fontSize: "0.75rem", color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.name}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Cull, Proofing, and Stats tab components moved to
// web/src/components/gallery/ — see imports at the top of this file.